import { FC, useCallback, useMemo } from 'react';

import cn from 'classnames';
import { dayjs } from 'utils/providers/Timezone';
import { TbDotsVertical } from 'react-icons/tb';
import { compact } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Dropdown, NavAction } from 'ui/components/nav';
import { DeleteItemModal } from 'ui/components/common';
import { useGetSubscription } from 'modules/account/settings/hooks';
import c, { SIZE } from 'utils/constants';
import { AvatarGroup } from 'ui/components/img/Avatar';
import useModal from 'ui/partials/Modal/useModal';
import { useCan as useCanRolePermissions } from 'utils/providers/RolePermission';
import { SimpleLead } from 'utils/types';
import { GoChevronDown } from 'react-icons/go';

import { useDeleteDeal, useGetStages, useUpdateDealStage } from '../hooks';
import _c from '../utils/constants';

const { ACTIONS, PERMISSION_KEYS } = c;

type PROPS = {
  name: string;
  amount: string;
  closedAt: Date;
  pipelineId: string;
  isSelected?: boolean;
  contacts?: string[] | SimpleLead[];
  company?: string;
  stageId: string;
  id: string;
};

const DealTile: FC<PROPS> = ({
  name,
  closedAt,
  amount,
  pipelineId,
  isSelected = false,
  contacts = [],
  company = null,
  stageId,
  id,
}) => {
  const navigate = useNavigate();

  const canEdit = useCanRolePermissions(ACTIONS.UPDATE, PERMISSION_KEYS.DEALS);
  const canDelete = useCanRolePermissions(
    ACTIONS.DELETE,
    PERMISSION_KEYS.DEALS,
  );

  const { data: subscription } = useGetSubscription();
  const { showModal } = useModal();
  const { data: stages } = useGetStages(pipelineId);
  const { mutate: updateDeal } = useUpdateDealStage();
  const { mutate: deleteDeal, isLoading: deleteLoading } = useDeleteDeal();

  const handleDeleteDeal = useCallback(() => {
    deleteDeal({ id, pipelineId });
  }, [deleteDeal, id, pipelineId]);

  const navOptions = useMemo(() => {
    const opts = [
      {
        text: 'View',
        key: 'view',
        onClick: () => navigate(`${id}?view=${_c.LIST}`),
      },
      canDelete &&
        !subscription?.isExpired && {
          text: 'Delete',
          key: 'delete',
          onClick: () => {
            showModal({
              title: 'Deal',
              content: (
                <DeleteItemModal
                  text="deal"
                  onClickDelete={handleDeleteDeal}
                  isLoading={deleteLoading}
                  validation
                />
              ),
            });
          },
        },
    ];

    return compact(opts);
  }, [
    canDelete,
    deleteLoading,
    handleDeleteDeal,
    id,
    showModal,
    subscription?.isExpired,
    navigate,
  ]);

  const handleChangeStage = (updatedStageId: string) => {
    updateDeal({
      stageId: updatedStageId,
      id,
    });
  };

  return (
    <div
      className={cn(
        'shadow-leapify-sm',
        'grid grid-cols-10',
        'rounded-[6px] bg-fill-tertiary py-5 pl-5',
        'border',
        isSelected ? 'border-secondary-base' : 'border-transparent',
        'items-center',
      )}
    >
      <p className="col-span-2 text-[14px] font-semibold leading-6">{name}</p>
      <p className="col-span-1 text-[14px] leading-6">
        {closedAt ? dayjs(closedAt).format(c.DATE_FORMAT_V2) : '---'}
      </p>
      <p className="col-span-1 text-[14px] leading-6">${amount}</p>
      <p className="col-span-2 text-[14px] leading-6">{company}</p>
      <AvatarGroup size={SIZE.Small} users={contacts} className="col-span-1" />
      <div className="col-span-2">
        <Dropdown
          customIcon={<GoChevronDown className="w-5 h-5 text-secondary-base" />}
          placeholder="Dropdown"
          defaultValue={stageId}
          value={stageId}
          options={
            stages?.map((stage) => ({
              label: stage.name,
              value: stage._id,
              isSelected: stage._id === stageId,
              onClick: () => {
                handleChangeStage(stage._id);
              },
            })) || []
          }
          selectedClassName="font-semibold bg-secondary-lightest w-[220px]"
          optionsClassName="w-[220px]"
          disabled={subscription?.isExpired || !canEdit}
        />
      </div>
      <div className="col-span-1 pr-5 justify-self-end">
        <NavAction
          label={<TbDotsVertical className="h-5 w-5 text-secondary" />}
          options={navOptions}
          optionsClassName="w-[100px] right-0"
        />
      </div>
    </div>
  );
};

export default DealTile;
